class PredictiveSearch extends HTMLElement {
  static MIN_QUERY_LENGTH = 2;

  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    // MAHA (2026-08-25): cierre por scroll. El buscador ahora puede quedar
    // pegado dentro de una cabecera sticky -- antes, con la cabecera
    // yendose con el scroll, el desplegable de resultados desaparecia solo
    // de la vista. Mismo patron que boundHandleClickOutside: un listener
    // conectado desde el constructor, que solo actua si el estado esta
    // abierto -- no un mecanismo nuevo.
    this.scrollYAtOpen = null;
    this.boundHandleScrollClose = this.handleScrollClose.bind(this);
    document.addEventListener('scroll', this.boundHandleScrollClose, { capture: true, passive: true });
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    this.scrollYAtOpen = window.scrollY;
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.abortController?.abort();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.clear();
      return;
    }
    if (query.length < this.constructor.MIN_QUERY_LENGTH) {
      this.abortController?.abort();
      this.removeAttribute('results');
      const target = document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
      if (target) target.innerHTML = '';
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.abortController?.abort();
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoadingState(true);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Una respuesta abortada (superada por una tecla más reciente) sigue
        // resolviendo su finally() de forma asíncrona; si para entonces ya hay
        // otro fetch en curso, no debe apagar SU estado "loading" ni marcar
        // "results" antes de tiempo. Solo actualiza si sigue siendo el fetch activo.
        if (this.abortController === controller) this.setLoadingState(false);
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleScrollClose(event) {
    // MAHA (2026-08-25): coalescido con requestAnimationFrame -- ver la misma
    // nota en header.js DetailsDropdown.handleScrollClose. Como maximo un
    // cuadro de trabajo real por rafaga de scroll, con el evento mas reciente.
    this._scrollCloseEvent = event;
    if (this._scrollCloseRafId) return;
    this._scrollCloseRafId = requestAnimationFrame(() => {
      this._scrollCloseRafId = null;
      this.processScrollClose(this._scrollCloseEvent);
    });
  }

  processScrollClose(event) {
    if (!this.classList.contains(this.states.OPEN)) return;
    // Un scroll DENTRO del panel de resultados (lista larga con su propio
    // overflow) dispara su propio evento con ESE elemento como target, nunca
    // "document" -- eso es el cliente leyendo resultados, no cerrando nada.
    if (event.target !== document && this.contains(event.target)) return;
    if (event.target !== document) return;
    if (this.scrollYAtOpen === null) return;

    if (Math.abs(window.scrollY - this.scrollYAtOpen) > 8) {
      const hadFocusInside = this.contains(document.activeElement);
      this.toggleSearchState(false);
      if (hadFocusInside) {
        // de vuelta al campo de texto, nunca dentro del panel oculto
        this.input.blur();
      }
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
